import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    TextField,
    Typography,
    Chip,
    CircularProgress,
    Link,
    Stack,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { getAllOrganizers, deleteOrganizer } from '../../firebase/firestore';

const OrganizerList = ({ onEdit }) => {
    const [organizers, setOrganizers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadOrganizers();
    }, []);

    const loadOrganizers = async () => {
        try {
            const data = await getAllOrganizers();
            setOrganizers(data);
        } catch (error) {
            console.error('Błąd podczas ładowania organizatorów:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleDelete = async (organizerId) => {
        if (window.confirm('Czy na pewno chcesz usunąć tego organizatora?')) {
            try {
                await deleteOrganizer(organizerId);
                await loadOrganizers();
            } catch (error) {
                console.error('Błąd podczas usuwania organizatora:', error);
            }
        }
    };

    const filteredOrganizers = organizers.filter(organizer =>
        organizer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        organizer.address.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Kompaktowe wyszukiwanie */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Szukaj organizatora..."
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'rgb(156, 163, 175)', mr: 1, fontSize: '1.2rem' }} />,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(76, 29, 149, 0.08)',
                            '& fieldset': {
                                borderColor: 'rgba(76, 29, 149, 0.2)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(76, 29, 149, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgb(196, 181, 253)',
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: 'white',
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>

            {/* Kompaktowa tabela */}
            <TableContainer component={Paper} sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                borderRadius: '0.5rem',
                border: '1px solid rgb(55, 65, 81)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                overflowX: 'auto'
            }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(76, 29, 149, 0.1)' }}>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>Nazwa</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>Adres</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>Strona WWW</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Akcje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredOrganizers
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((organizer) => (
                                <TableRow
                                    key={organizer.id}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.08)',
                                        },
                                        transition: 'background-color 0.2s'
                                    }}
                                >
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{organizer.name}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        maxWidth: '250px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{organizer.address}</TableCell>
                                    <TableCell sx={{
                                        fontSize: '0.75rem'
                                    }}>
                                        {organizer.website ? (
                                            <Link
                                                href={organizer.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: 'rgb(196, 181, 253)',
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    },
                                                    fontSize: '0.75rem',
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    display: 'block'
                                                }}
                                            >
                                                {organizer.website}
                                            </Link>
                                        ) : (
                                            <Chip
                                                label="Brak"
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                                                    color: 'rgb(156, 163, 175)',
                                                    fontSize: '0.65rem',
                                                    height: '20px'
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Edytuj">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(organizer)}
                                                    sx={{
                                                        color: 'rgb(196, 181, 253)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Usuń">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(organizer.id)}
                                                    sx={{
                                                        color: '#ef4444',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredOrganizers.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 15, 25, 50]}
                    sx={{
                        color: 'rgb(196, 181, 253)',
                        '.MuiTablePagination-select': {
                            color: 'rgb(196, 181, 253)',
                        },
                        '.MuiTablePagination-selectIcon': {
                            color: 'rgb(196, 181, 253)',
                        },
                        '.MuiIconButton-root': {
                            color: 'rgb(196, 181, 253)',
                            '&:hover': {
                                backgroundColor: 'rgba(196, 181, 253, 0.1)',
                            },
                            '&.Mui-disabled': {
                                color: 'rgb(75, 85, 99)',
                            }
                        }
                    }}
                />
            </TableContainer>
        </Box>
    );
};

export default OrganizerList; 